import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileText, Loader2, Search } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionEyebrow from "@/components/SectionEyebrow";
import Seo from "@/components/Seo";
import { fetchDownloads, retrieveDownloads, apiErr } from "@/lib/api";
import { breadcrumbJsonLd } from "@/lib/seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function DownloadCard({ d }) {
    return (
        <Link to={`/downloads/${d.slug}`} data-testid={`download-card-${d.slug}`}
            className="glass glow-border group block overflow-hidden rounded-lg transition hover:border-gold-300/40">
            <div className="aspect-video w-full overflow-hidden bg-white/[0.03]">
                {d.image ? (
                    <img src={`${BACKEND}${d.image}`} alt={d.title} loading="lazy"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" />
                ) : (
                    <div className="flex h-full w-full items-center justify-center"><FileText size={40} className="text-gold-300/40" /></div>
                )}
            </div>
            <div className="p-6">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider">
                    {d.category && <span className="rounded-full bg-white/5 px-2.5 py-1 text-white/50">{d.category}</span>}
                    {d.file_type && <span className="rounded-full bg-white/5 px-2.5 py-1 text-white/50">{d.file_type.toUpperCase()}{d.file_size ? ` · ${d.file_size}` : ""}</span>}
                </div>
                <h3 className="mt-3 font-display text-lg leading-snug">{d.title}</h3>
                {d.short_description && <p className="mt-2 text-sm leading-relaxed text-white/55 line-clamp-2">{d.short_description}</p>}
                <div className="mt-4 flex items-center justify-between">
                    {d.type === "free" ? (
                        <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-medium uppercase tracking-wider text-emerald-300">Free</span>
                    ) : (
                        <p className="font-display text-lg gold-text">₹{(d.price || 0).toLocaleString("en-IN")}
                            {d.strike_price && <span className="ml-2 text-sm text-white/35 line-through">₹{d.strike_price.toLocaleString("en-IN")}</span>}
                        </p>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-xs text-gold-300"><Download size={13} /> {d.type === "free" ? "Download" : "Buy & Download"}</span>
                </div>
            </div>
        </Link>
    );
}

function RetrieveBox() {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState(null);
    const run = async () => {
        if (!email || !mobile) return toast.error("Enter the email and mobile used at purchase.");
        setBusy(true);
        try {
            const r = await retrieveDownloads({ email, mobile });
            setResults(r.purchases || []);
            if (!(r.purchases || []).length) toast.info("No purchases found for these details.");
        } catch (e) { toast.error(apiErr(e, "Could not look up purchases.")); }
        finally { setBusy(false); }
    };
    const inputCls = "h-11 w-full rounded-md border border-white/10 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-gold-300/50 focus:outline-none";
    return (
        <div className="glass glow-border rounded-lg p-6 md:p-8" data-testid="retrieve-purchases-box">
            <button onClick={() => setOpen(!open)} data-testid="retrieve-toggle" className="flex w-full items-center justify-between text-left">
                <div>
                    <p className="font-display text-lg">Already purchased? <span className="gold-text">Retrieve your downloads</span></p>
                    <p className="mt-1 text-sm text-white/50">Enter the email and mobile you used while paying — we'll show your secure download links.</p>
                </div>
                <Search size={18} className="flex-none text-gold-300" />
            </button>
            {open && (
                <div className="mt-5">
                    <div className="grid gap-3 sm:grid-cols-3">
                        <input data-testid="retrieve-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
                        <input data-testid="retrieve-mobile" className={inputCls} value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" />
                        <button data-testid="retrieve-submit" onClick={run} disabled={busy} className="gold-button inline-flex items-center justify-center gap-2 disabled:opacity-60">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Find My Downloads
                        </button>
                    </div>
                    {results && results.length > 0 && (
                        <div className="mt-5 space-y-2" data-testid="retrieve-results">
                            {results.map((r) => (
                                <div key={r.order_code} className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-white/[0.03] px-4 py-3">
                                    <div><p className="text-sm text-white/85">{r.title}</p><p className="text-xs text-white/40">{r.order_code} · {(r.purchased_at || "").slice(0, 10)}</p></div>
                                    <a href={`${BACKEND}${r.download_url}`} className="ghost-button inline-flex items-center gap-2 !px-4 !py-2 text-xs"><Download size={13} /> Download</a>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function Downloads() {
    const [items, setItems] = useState(null);
    useEffect(() => { fetchDownloads().then(setItems).catch(() => setItems([])); }, []);
    return (
        <div data-testid="downloads-page" className="relative">
            <Seo
                title="Free & Premium Business Downloads — Templates, Guides & Toolkits | Abhijit JK"
                description="Download practical business growth resources by Abhijit JK — free and premium templates, checklists, guides and toolkits for founders, MSMEs and marketing teams."
                path="/downloads"
                jsonLd={[breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Downloads", path: "/downloads" }])]}
            />
            <section className="mx-auto max-w-7xl px-6 pt-16 pb-10 lg:px-10">
                <Reveal><SectionEyebrow>Download Now</SectionEyebrow></Reveal>
                <Reveal delay={0.1}>
                    <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.08] tracking-tighter sm:text-5xl lg:text-6xl">
                        Practical resources you can <span className="gold-text">use today.</span>
                    </h1>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/65 md:text-lg">
                        Templates, guides, checklists and toolkits distilled from 15+ years of real
                        consulting — some free, some premium. Instant, secure delivery.
                    </p>
                </Reveal>
            </section>
            <section className="mx-auto max-w-7xl px-6 pb-10 lg:px-10">
                {items === null ? (
                    <p className="text-white/50">Loading downloads…</p>
                ) : items.length === 0 ? (
                    <div className="glass rounded-lg p-10 text-center text-white/60" data-testid="downloads-empty">
                        New downloads are being prepared. Check back soon.
                    </div>
                ) : (
                    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                        {items.map((d) => <Reveal key={d.id}><DownloadCard d={d} /></Reveal>)}
                    </div>
                )}
            </section>
            <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
                <Reveal><RetrieveBox /></Reveal>
            </section>
        </div>
    );
}
